/**
 * Venue-local formatting helpers (localization-i18n R5): every diner-facing date/time renders in the
 * VENUE timezone (DOMAIN.md §B.4), never the device's. Built on native `Intl` (no date library,
 * ADR-0013), so a booking made in EN shows EN copy while the slot times stay venue-local — proven by
 * the `TZ=…` vitest run. The widget computes NO availability math; these only render given instants.
 */

function isEnglish(locale: string): boolean {
  return locale.toLowerCase().startsWith("en");
}

/** A single slot time, e.g. `19:30` (fr) / `7:30 PM` (en), in the venue's wall clock. */
export function formatSlotTime(startSec: number, timeZone: string, locale: string): string {
  const en = isEnglish(locale);
  return new Intl.DateTimeFormat(en ? "en-US" : "fr-FR", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: en,
  }).format(new Date(startSec * 1000));
}

/** A long service date, e.g. `samedi 1 août 2026` / `Saturday, August 1, 2026`, venue-local. */
export function formatServiceDate(serviceDay: string, timeZone: string, locale: string): string {
  const [y, m, d] = serviceDay.split("-").map(Number);
  // Anchor at noon UTC so the venue-local date never rolls to the neighbouring day.
  const date = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1, 12, 0, 0));
  return new Intl.DateTimeFormat(isEnglish(locale) ? "en-US" : "fr-FR", {
    timeZone,
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
}

/** `m:ss` remaining, e.g. `1:59` — the visible hold countdown (never negative). */
export function formatCountdown(remainingSec: number): string {
  const s = Math.max(0, Math.floor(remainingSec));
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${mm}:${String(ss).padStart(2, "0")}`;
}

/** Today's calendar date (`YYYY-MM-DD`) in the venue timezone — the date picker's `min`. */
export function todayInTz(timeZone: string, at: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(at);
  const map: Record<string, string> = {};
  for (const p of parts) map[p.type] = p.value;
  return `${map.year}-${map.month}-${map.day}`;
}

/** Add `days` to a `YYYY-MM-DD` date, returning `YYYY-MM-DD` (UTC calendar math — no tz drift). */
export function addDaysIso(iso: string, days: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  const date = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1));
  date.setUTCDate(date.getUTCDate() + days);
  const y2 = date.getUTCFullYear();
  const m2 = String(date.getUTCMonth() + 1).padStart(2, "0");
  const d2 = String(date.getUTCDate()).padStart(2, "0");
  return `${y2}-${m2}-${d2}`;
}

/** The UTC offset (minutes east of UTC) of `timeZone` at the given instant. */
function tzOffsetMinutes(date: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(date);
  const map: Record<string, string> = {};
  for (const p of parts) map[p.type] = p.value;
  const asUtc = Date.UTC(
    Number(map.year),
    Number(map.month) - 1,
    Number(map.day),
    Number(map.hour === "24" ? "0" : map.hour),
    Number(map.minute),
    Number(map.second),
  );
  return (asUtc - date.getTime()) / 60000;
}

/**
 * Convert a venue-local wall clock (`serviceDay` `YYYY-MM-DD` + `time` `HH:mm`) to an epoch-second
 * instant. Used ONLY by the large-party request path (SC-5), which never issues a slot query and so
 * carries a provisional desired time rather than a live slot. Correct except within the one-hour DST
 * fold, where it may be off by an hour — acceptable for a request the venue re-confirms.
 */
export function wallClockToEpochSec(serviceDay: string, time: string, timeZone: string): number {
  const [y, m, d] = serviceDay.split("-").map(Number);
  const [hh, mm] = time.split(":").map(Number);
  const guess = Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1, hh ?? 0, mm ?? 0);
  const offset = tzOffsetMinutes(new Date(guess), timeZone);
  return Math.floor((guess - offset * 60000) / 1000);
}
